import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import { SidebarService } from '../../../services/side-bar.service';
import { ClothingService } from '../../../services/clothing.service';
import { ClothingErrorService } from '../../../services/clothing-error.service';
import { AdminService } from '../../../services/admin.service';
import {
  ClothingCatalogItemList,
  ClothingIssueStats,
  ClothingIssueStatsFilters,
  ClothingOption,
  ClothingUserOption
} from '../../../models/clothing.model';
import { CLOTHING_MENU_OPTIONS } from './clothing.constants';

const CLOTHING_REPORT_ALL_TIME_FROM = '2020-01-01';

interface ClothingUsersListItem {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  roles: string[];
}

interface ClothingReportFilterData {
  catalogItems: ClothingOption[];
  storekeepers: ClothingUserOption[];
}

@Component({
  selector: 'app-clothing-report',
  templateUrl: './clothing-report.component.html',
  styleUrls: ['./clothing-report.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminClothingReportComponent implements OnInit, OnDestroy {
  catalogItems: ClothingOption[] = [];
  rows: ClothingIssueStats[] = [];
  storekeepers: ClothingUserOption[] = [];
  useCatalogItemFilter = false;
  useStorekeeperFilter = false;
  selectedCatalogItemId = '';
  selectedStorekeeperUserId = '';
  fromDate = '';
  toDate = '';
  errorMessage = '';
  isLoading = false;
  isLoadingFilters = false;
  private destroy$ = new Subject<void>();

  constructor(
    private adminService: AdminService,
    private clothingService: ClothingService,
    private clothingErrorService: ClothingErrorService,
    private sidebarService: SidebarService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.sidebarService.setSelectedMenuOption(CLOTHING_MENU_OPTIONS.report);
    this.loadFilterOptions();
    this.loadReport();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get totalIssuedQuantity(): number {
    return this.rows.reduce((sum, row) => sum + row.issuedQuantity, 0);
  }

  get totalOperations(): number {
    return this.rows.reduce((sum, row) => sum + row.issueOperationsCount, 0);
  }

  get totalAmountUsd(): number {
    return this.rows.reduce((sum, row) => sum + row.totalIssuedUsd, 0);
  }

  loadReport() {
    this.clearMessages();
    this.isLoading = true;
    this.clothingService.getIssuedStats(this.createReportFilters())
      .takeUntil(this.destroy$)
      .subscribe(
        (rows) => this.handleReportLoaded(rows),
        (error: HttpErrorResponse) => this.handleReportLoadFailed(error)
      );
  }

  trackByRow(index: number, row: ClothingIssueStats): string {
    return row.catalogItemId;
  }

  trackByOption(index: number, option: ClothingOption): string {
    return option.id;
  }

  trackByStorekeeper(index: number, storekeeper: ClothingUserOption): string {
    return storekeeper.id;
  }

  toggleCatalogItemFilter() {
    this.useCatalogItemFilter = !this.useCatalogItemFilter;
    this.clearCatalogItemValue();
  }

  toggleStorekeeperFilter() {
    this.useStorekeeperFilter = !this.useStorekeeperFilter;
    this.clearStorekeeperValue();
  }

  private loadFilterOptions() {
    this.isLoadingFilters = true;
    Observable.forkJoin(
      this.clothingService.getCatalogItems(false),
      this.adminService.getAllFullUsers()
    )
      .takeUntil(this.destroy$)
      .subscribe(
        (response: [ClothingCatalogItemList[], HttpResponse<ClothingUsersListItem[]>]) =>
          this.handleFilterOptionsLoaded(this.createFilterData(response)),
        (error: HttpErrorResponse) => this.handleFilterOptionsFailed(error)
      );
  }

  private createFilterData(
    response: [ClothingCatalogItemList[], HttpResponse<ClothingUsersListItem[]>]
  ): ClothingReportFilterData {
    return {
      catalogItems: this.mapCatalogItems(response[0]),
      storekeepers: this.mapStorekeepers(response[1])
    };
  }

  private mapCatalogItems(items: ClothingCatalogItemList[]): ClothingOption[] {
    return this.sortOptions(items.map((item) => this.toOption(item)));
  }

  private mapStorekeepers(
    response: HttpResponse<ClothingUsersListItem[]>
  ): ClothingUserOption[] {
    var users = response.body || [];

    return this.sortUsers(
      users
        .filter((user) => this.hasStorekeeperRole(user.roles))
        .map((user) => this.createStorekeeperOption(user))
    );
  }

  private handleFilterOptionsLoaded(data: ClothingReportFilterData) {
    this.catalogItems = data.catalogItems;
    this.storekeepers = data.storekeepers;
    this.isLoadingFilters = false;
    this.markForCheck();
  }

  private handleFilterOptionsFailed(error: HttpErrorResponse) {
    this.isLoadingFilters = false;
    this.showHttpError(error);
  }

  private handleReportLoaded(rows: ClothingIssueStats[]) {
    this.rows = rows;
    this.isLoading = false;
    this.markForCheck();
  }

  private handleReportLoadFailed(error: HttpErrorResponse) {
    this.isLoading = false;
    this.showHttpError(error);
  }

  private formatDate(date: Date): string {
    var month = this.formatDatePart(date.getMonth() + 1);
    var day = this.formatDatePart(date.getDate());

    return date.getFullYear() + '-' + month + '-' + day;
  }

  private formatDatePart(value: number): string {
    if (value < 10) {
      return '0' + value;
    }
    return String(value);
  }

  private createReportFilters(): ClothingIssueStatsFilters {
    return {
      from: this.resolveFromDate(),
      to: this.resolveToDate(),
      catalogItemId: this.resolveCatalogItemId(),
      storekeeperUserId: this.resolveStorekeeperUserId()
    };
  }

  private resolveFromDate(): string {
    if (this.fromDate.length) {
      return this.fromDate;
    }
    return CLOTHING_REPORT_ALL_TIME_FROM;
  }

  private resolveToDate(): string {
    if (this.toDate.length) {
      return this.toDate;
    }
    return this.formatDate(new Date());
  }

  private resolveCatalogItemId(): string {
    if (!this.useCatalogItemFilter) {
      return '';
    }
    return this.selectedCatalogItemId;
  }

  private resolveStorekeeperUserId(): string {
    if (!this.useStorekeeperFilter) {
      return '';
    }
    return this.selectedStorekeeperUserId;
  }

  private createStorekeeperOption(user: ClothingUsersListItem): ClothingUserOption {
    return {
      id: user.id,
      fullName: this.resolveFullName(user)
    };
  }

  private toOption(item: ClothingOption): ClothingOption {
    return {
      id: item.id,
      name: item.name
    };
  }

  private resolveFullName(user: ClothingUsersListItem): string {
    if (user.fullName && user.fullName.length) {
      return user.fullName;
    }
    return (user.lastName + ' ' + user.firstName).trim();
  }

  private hasStorekeeperRole(roles: string[]): boolean {
    if (!roles || !roles.length) {
      return false;
    }
    if (roles.indexOf('STOREKEEPER') !== -1) {
      return true;
    }
    return roles.indexOf('ROLE_STOREKEEPER') !== -1;
  }

  private sortOptions(items: ClothingOption[]): ClothingOption[] {
    return items.slice().sort((left, right) => left.name.localeCompare(right.name));
  }

  private sortUsers(items: ClothingUserOption[]): ClothingUserOption[] {
    return items.slice().sort((left, right) => left.fullName.localeCompare(right.fullName));
  }

  private clearCatalogItemValue() {
    if (this.useCatalogItemFilter) {
      return;
    }
    this.selectedCatalogItemId = '';
  }

  private clearStorekeeperValue() {
    if (this.useStorekeeperFilter) {
      return;
    }
    this.selectedStorekeeperUserId = '';
  }

  private clearMessages() {
    this.errorMessage = '';
  }

  private showHttpError(error: HttpErrorResponse) {
    this.errorMessage = this.resolveErrorMessage(error);
    this.markForCheck();
  }

  private resolveErrorMessage(error: HttpErrorResponse): string {
    return this.clothingErrorService.resolveDefaultMessage(error);
  }

  private markForCheck() {
    this.changeDetectorRef.markForCheck();
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/admin/clothing/clothing-report.component.ts